import { Product } from "@/types/product";
import { PaginatedResponse } from "@/types/types";
import { User } from "@/types/user";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  createProduct,
  updateProduct,
  createProductRecipe,
  updateProductRecipe,
  restockProduct,
  StoreRecipePayload,
  UpdateRecipePayload,
} from "./api";

export function useCreateProduct() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (values: Product) => createProduct(values),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["products"] });
    },
  });
}

export function useUpdateProduct() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (values: Product) => updateProduct(values),

    onSuccess: (_response, values) => {
      // 1️⃣ obtener todas las queries relacionadas con "users"
      const queries = queryClient.getQueriesData<PaginatedResponse<User>>({
        queryKey: ["products"],
      });

      // 2️⃣ iterar cada cache (paginaciones, filtros, etc.)
      queries.forEach(([queryKey, oldData]) => {
        if (!oldData) return;

        // 3️⃣ reemplazar el usuario si coincide el id
        const updatedItems = oldData.items.map((user) =>
          user.id === values.id ? { ...user, ...values } : user,
        );

        // 4️⃣ actualizar cache
        queryClient.setQueryData(queryKey, {
          ...oldData,
          items: updatedItems,
        });
      });
    },
  });
}

export function useCreateProductRecipe() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      productId,
      payload,
    }: {
      productId: string;
      payload: StoreRecipePayload;
    }) => createProductRecipe(productId, payload),
    onSuccess: (data, { productId }) => {
      queryClient.setQueryData(["productRecipe", productId], data);
      queryClient.invalidateQueries({ queryKey: ["productRecipe", productId] });
    },
  });
}

export function useUpdateProductRecipe() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      productId,
      payload,
    }: {
      productId: string;
      payload: UpdateRecipePayload;
    }) => updateProductRecipe(productId, payload),
    onSuccess: (data, { productId }) => {
      queryClient.setQueryData(["productRecipe", productId], data);
      queryClient.invalidateQueries({ queryKey: ["productRecipe", productId] });
    },
  });
}

export function useRestockProduct() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      productId,
      quantity,
    }: {
      productId: string;
      quantity: number;
    }) => restockProduct(productId, quantity),
    onSuccess: () => {
      // Invalidate queries to refresh the stocks of ingredients in the UI
      queryClient.invalidateQueries({ queryKey: ["ingredients"] });
      queryClient.invalidateQueries({ queryKey: ["products"] });
    },
  });
}

